import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { getPublicTournamentApi } from "../api/tournament.api";
import { getPublicMatchesApi, getPublicStandingsApi } from "../api/match.api";

import Icon from "../components/common/Icon";
import { toJalali, toJalaliFull } from "../utils/jalali";
import {
  TOURNAMENT_TYPE_LABELS,
  TOURNAMENT_STATUS_LABELS,
  MATCH_STATUS_LABELS,
  STAGE_TYPE_LABELS,
} from "../utils/constants";

const STATUS_THEME = {
  OPEN: { chip: "bg-blue-500/10 text-blue-200 border-blue-400/20", dot: "bg-blue-400" },
  ONGOING: { chip: "bg-emerald-500/10 text-emerald-200 border-emerald-400/20", dot: "bg-emerald-400" },
  COMPLETED: { chip: "bg-white/10 text-white/70 border-white/15", dot: "bg-white/50" },
  CLOSED: { chip: "bg-red-500/10 text-red-200 border-red-400/20", dot: "bg-red-400" },
};

const TABS = [
  { key: "overview", label: "نمای کلی", icon: "trophy" },
  { key: "standings", label: "جدول رده‌ بندی", icon: "report" },
  { key: "matches", label: "مسابقات", icon: "ball" },
];

function getImageUrl(path) {
  if (!path) return null;
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
    return path;
  }
  const base =
    process.env.REACT_APP_API_BASE_URL?.replace(/\/api\/v1\/?$/, "") ||
    window.location.origin;
  return `${base}${path.startsWith("/") ? "" : "/"}${path}`;
}

/* =========================================================
   HEADER
========================================================= */
function TournamentHero({ tournament }) {
  const theme = STATUS_THEME[tournament.status] || STATUS_THEME.OPEN;

  return (
    <section className="hero-panel relative isolate mx-3 overflow-hidden rounded-[2.25rem] bg-[#07150F] shadow-[0_50px_100px_-40px_rgba(7,21,15,0.7)] sm:mx-5 sm:rounded-[3rem]">
      <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-emerald-500/15 blur-3xl" />
      <div className="pointer-events-none absolute -left-40 bottom-0 h-[420px] w-[420px] rounded-full bg-lime-500/10 blur-3xl" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-px w-full -translate-x-1/2 bg-gradient-to-l from-transparent via-white/20 to-transparent" />

      <div className="container-page relative py-16 sm:py-20 lg:py-24">
        <Link
          to="/tournaments"
          className="inline-flex items-center gap-1.5 text-xs font-bold text-white/50 transition hover:text-white"
        >
          <Icon name="chevronLeft" className="h-3.5 w-3.5 rotate-180" />
          بازگشت به فهرست تورنمنت‌ها
        </Link>

        <div className="mt-6 flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-5">
            <div className="grid h-20 w-20 shrink-0 place-items-center rounded-[1.75rem] bg-gradient-to-br from-emerald-400/20 to-lime-400/10 text-emerald-300 ring-1 ring-white/10 backdrop-blur sm:h-24 sm:w-24">
              <Icon name="trophy" className="h-10 w-10 sm:h-12 sm:w-12" />
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-black ${theme.chip}`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${theme.dot} ${
                      tournament.status === "ONGOING" ? "animate-pulse" : ""
                    }`}
                  />
                  {TOURNAMENT_STATUS_LABELS[tournament.status] || tournament.status}
                </span>

                <span className="rounded-full bg-white/10 px-3 py-1 text-[11px] font-black text-white/60">
                  {TOURNAMENT_TYPE_LABELS[tournament.type] || tournament.type}
                </span>
              </div>

              <h1 className="mt-3 max-w-2xl text-3xl font-black leading-tight text-white sm:text-4xl lg:text-5xl">
                {tournament.name}
              </h1>

              <div className="mt-3 flex items-center gap-2 text-xs font-bold text-white/50 sm:text-sm">
                <Icon name="calendar" className="h-4 w-4" />
                {toJalaliFull(tournament.startDate)}
                {tournament.endDate && (
                  <>
                    <span className="text-white/25">تا</span>
                    {toJalaliFull(tournament.endDate)}
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:w-auto">
            <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 text-center backdrop-blur-xl">
              <strong className="block text-2xl font-black text-white">
                {(tournament.clubsCount ?? 0).toLocaleString("fa-IR")}
              </strong>
              <span className="text-[10px] text-white/40">باشگاه</span>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 text-center backdrop-blur-xl">
              <strong className="block text-2xl font-black text-white">
                {(tournament._count?.matches ?? 0).toLocaleString("fa-IR")}
              </strong>
              <span className="text-[10px] text-white/40">مسابقه</span>
            </div>

            <div className="rounded-[1.5rem] border border-white/10 bg-white/[0.06] p-4 text-center backdrop-blur-xl">
              <strong className="block text-2xl font-black text-white">
                {(tournament.stages?.length ?? 0).toLocaleString("fa-IR")}
              </strong>
              <span className="text-[10px] text-white/40">مرحله</span>
            </div>
          </div>
        </div>

        {tournament.description && (
          <p className="mt-6 max-w-3xl text-sm leading-8 text-white/55">
            {tournament.description}
          </p>
        )}
      </div>
    </section>
  );
}

/* =========================================================
   TABS
========================================================= */
function TabBar({ active, onChange }) {
  return (
    <div className="relative z-10 -mt-8 mb-10 flex w-fit gap-1 rounded-2xl border border-slate-200/80 bg-white/95 p-1.5 shadow-2xl shadow-slate-900/[0.12] backdrop-blur-xl">
      {TABS.map((tab) => (
        <button
          key={tab.key}
          type="button"
          onClick={() => onChange(tab.key)}
          className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-xs font-black transition ${
            active === tab.key
              ? "bg-[#0B1F17] text-white shadow-lg"
              : "text-slate-500 hover:bg-slate-50"
          }`}
        >
          <Icon name={tab.icon} className="h-4 w-4" />
          {tab.label}
        </button>
      ))}
    </div>
  );
}

/* =========================================================
   OVERVIEW TAB
========================================================= */
function OverviewTab({ tournament }) {
  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      {/* Clubs */}
      <div className="lg:col-span-2">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-black text-slate-700">
          <Icon name="shield" className="h-4 w-4 text-emerald-500" />
          باشگاه‌های حاضر ({(tournament.clubs?.length ?? 0).toLocaleString("fa-IR")})
        </h3>

        {!tournament.clubs || tournament.clubs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-xs text-slate-400">
            هنوز باشگاهی در این تورنمنت تایید نشده است.
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {tournament.clubs.map((club) => (
              <Link
                key={club.id}
                to={`/clubs/${club.id}`}
                className="group flex flex-col items-center gap-2.5 rounded-2xl border border-slate-100 bg-white p-4 text-center shadow-sm transition hover:-translate-y-1 hover:border-emerald-200 hover:shadow-lg"
              >
                <div className="grid h-14 w-14 place-items-center overflow-hidden rounded-2xl bg-slate-50 ring-1 ring-slate-100">
                  {club.logo ? (
                    <img
                      src={getImageUrl(club.logo)}
                      alt={club.name}
                      className="h-10 w-10 object-contain"
                    />
                  ) : (
                    <Icon name="shield" className="h-6 w-6 text-slate-300" />
                  )}
                </div>
                <span className="truncate text-xs font-black text-slate-700 transition group-hover:text-emerald-600">
                  {club.name}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Stages */}
      <div>
        <h3 className="mb-4 flex items-center gap-2 text-sm font-black text-slate-700">
          <Icon name="medal" className="h-4 w-4 text-blue-500" />
          مراحل رقابت
        </h3>

        {!tournament.stages || tournament.stages.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-xs text-slate-400">
            هنوز مرحله‌ای برای این تورنمنت تعریف نشده است.
          </div>
        ) : (
          <div className="space-y-3">
            {tournament.stages.map((stage) => (
              <div
                key={stage.id}
                className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-black text-slate-800">{stage.name}</span>
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500">
                    {STAGE_TYPE_LABELS[stage.type] || stage.type}
                  </span>
                </div>

                {stage.groups?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {stage.groups.map((group) => (
                      <span
                        key={group.id}
                        className="rounded-lg bg-slate-50 px-2 py-1 text-[10px] font-bold text-slate-500"
                      >
                        {group.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================
   STANDINGS TAB
========================================================= */
const MEDALS = ["🥇", "🥈", "🥉"];

function StandingsTable({ rows }) {
  return (
    <div className="overflow-hidden rounded-[2rem] border border-slate-200/80 bg-white shadow-xl shadow-slate-900/[0.05]">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] text-center text-xs">
          <thead>
            <tr className="bg-[#0B1F17] text-white/70">
              <th className="px-4 py-4 text-right font-black">تیم</th>
              <th className="px-3 py-4 font-black">بازی</th>
              <th className="px-3 py-4 font-black">برد</th>
              <th className="px-3 py-4 font-black">مساوی</th>
              <th className="px-3 py-4 font-black">باخت</th>
              <th className="px-3 py-4 font-black">گل زده</th>
              <th className="px-3 py-4 font-black">گل خورده</th>
              <th className="px-3 py-4 font-black">تفاضل</th>
              <th className="px-3 py-4 font-black text-emerald-300">امتیاز</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((row, index) => (
              <tr
                key={row.club?.id ?? index}
                className={`transition hover:bg-emerald-50/40 ${
                  index < 3 ? "bg-emerald-50/20" : ""
                }`}
              >
                <td className="px-4 py-3.5 text-right">
                  <div className="flex items-center gap-3">
                    <span className="w-6 shrink-0 text-sm font-black text-slate-400">
                      {MEDALS[index] || index + 1}
                    </span>

                    <div className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-xl bg-slate-50 ring-1 ring-slate-100">
                      {row.club?.logo ? (
                        <img
                          src={getImageUrl(row.club.logo)}
                          alt={row.club?.name}
                          className="h-6 w-6 object-contain"
                        />
                      ) : (
                        <Icon name="shield" className="h-4 w-4 text-slate-300" />
                      )}
                    </div>

                    <span className="truncate text-sm font-black text-slate-800">
                      {row.club?.name || "نامشخص"}
                    </span>
                  </div>
                </td>

                <td className="px-3 py-3.5 font-bold text-slate-600">{row.played}</td>
                <td className="px-3 py-3.5 font-bold text-emerald-600">{row.won}</td>
                <td className="px-3 py-3.5 font-bold text-amber-600">{row.drawn}</td>
                <td className="px-3 py-3.5 font-bold text-red-500">{row.lost}</td>
                <td className="px-3 py-3.5 font-bold text-slate-600">{row.goalsFor}</td>
                <td className="px-3 py-3.5 font-bold text-slate-600">{row.goalsAgainst}</td>
                <td className="px-3 py-3.5 font-mono font-bold text-slate-600">
                  {row.goalDiff > 0 ? `+${row.goalDiff}` : row.goalDiff}
                </td>
                <td className="px-3 py-3.5">
                  <span className="inline-flex min-w-[2rem] items-center justify-center rounded-lg bg-emerald-500 px-2 py-1 text-sm font-black text-white">
                    {row.points}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StandingsTab({ standings, groups, loading }) {
  if (loading) {
    return (
      <div className="h-96 animate-pulse rounded-[2rem] bg-white shadow-sm" />
    );
  }

  const hasGroups = Array.isArray(groups) && groups.length > 0;
  const hasFlatStandings = Array.isArray(standings) && standings.length > 0;

  if (!hasGroups && !hasFlatStandings) {
    return (
      <div className="rounded-[2.25rem] border border-dashed border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-50 text-slate-300">
          <Icon name="report" className="h-8 w-8" />
        </div>
        <h3 className="mt-4 text-lg font-black text-slate-700">
          هنوز جدول رده‌ بندی موجود نیست
        </h3>
        <p className="mt-2 text-xs text-slate-400">
          به‌ محض برگزاری اولین مسابقات، جدول اینجا نمایش داده می‌ شود.
        </p>
      </div>
    );
  }

  if (hasGroups) {
    return (
      <div className="space-y-8">
        {groups.map((group) => (
          <div key={group.id}>
            <h3 className="mb-4 flex items-center gap-2 text-sm font-black text-slate-700">
              <Icon name="medal" className="h-4 w-4 text-blue-500" />
              {group.name}
              {group.stageName && (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-bold text-slate-400">
                  {group.stageName}
                </span>
              )}
            </h3>

            {group.standings?.length > 0 ? (
              <StandingsTable rows={group.standings} />
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-6 text-center text-xs text-slate-400">
                هنوز مسابقه‌ای در این گروه برگزار نشده است.
              </div>
            )}
          </div>
        ))}

        {hasFlatStandings && (
          <div>
            <h3 className="mb-4 flex items-center gap-2 text-sm font-black text-slate-700">
              <Icon name="userGroup" className="h-4 w-4 text-slate-400" />
              بدون گروه
            </h3>
            <StandingsTable rows={standings} />
          </div>
        )}
      </div>
    );
  }

  return <StandingsTable rows={standings} />;
}

/* =========================================================
   MATCHES TAB
========================================================= */
function MatchRow({ match }) {
  const isPlayed = match.status === "PLAYED";

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:border-emerald-200 hover:shadow-lg sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3 text-[11px] font-bold text-slate-400">
        <Icon name="calendar" className="h-3.5 w-3.5" />
        {toJalali(match.date)}
        {match.time && (
          <>
            <span className="text-slate-200">|</span>
            <Icon name="clock" className="h-3.5 w-3.5" />
            {match.time}
          </>
        )}
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 sm:flex-1 sm:justify-center">
        <div className="flex min-w-0 items-center justify-end gap-2">
          <span className="truncate text-sm font-black text-slate-800">
            {match.homeClub?.name || "میزبان"}
          </span>
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-100">
            {match.homeClub?.logo ? (
              <img
                src={getImageUrl(match.homeClub.logo)}
                alt=""
                className="h-6 w-6 object-contain"
              />
            ) : (
              <Icon name="shield" className="h-4 w-4 text-slate-300" />
            )}
          </div>
        </div>

        {isPlayed ? (
          <div
            dir="ltr"
            className="rounded-xl bg-slate-900 px-3 py-1.5 text-sm font-black text-white"
          >
            {match.result?.awayScore ?? 0}
            <span className="mx-1.5 text-slate-500">-</span>
            {match.result?.homeScore ?? 0}
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-50 px-3 py-1.5 text-xs font-black text-emerald-600 ring-1 ring-emerald-100">
            VS
          </div>
        )}

        <div className="flex min-w-0 items-center gap-2">
          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-slate-50 ring-1 ring-slate-100">
            {match.awayClub?.logo ? (
              <img
                src={getImageUrl(match.awayClub.logo)}
                alt=""
                className="h-6 w-6 object-contain"
              />
            ) : (
              <Icon name="shield" className="h-4 w-4 text-slate-300" />
            )}
          </div>
          <span className="truncate text-sm font-black text-slate-800">
            {match.awayClub?.name || "مهمان"}
          </span>
        </div>
      </div>

      <span
        className={`inline-flex w-fit shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-[10px] font-black ${
          isPlayed
            ? "bg-slate-100 text-slate-500"
            : "bg-blue-50 text-blue-600"
        }`}
      >
        {MATCH_STATUS_LABELS[match.status] || match.status}
      </span>
    </div>
  );
}

function MatchesTab({ matches, loading }) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-white shadow-sm" />
        ))}
      </div>
    );
  }

  if (!matches || matches.length === 0) {
    return (
      <div className="rounded-[2.25rem] border border-dashed border-slate-200 bg-white px-6 py-16 text-center shadow-sm">
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-2xl bg-slate-50 text-slate-300">
          <Icon name="ball" className="h-8 w-8" />
        </div>
        <h3 className="mt-4 text-lg font-black text-slate-700">
          هنوز مسابقه‌ای برگزار نشده است
        </h3>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {matches.map((match) => (
        <MatchRow key={match.id} match={match} />
      ))}
    </div>
  );
}

/* =========================================================
   PAGE
========================================================= */
export default function TournamentDetails() {
  const { id } = useParams();

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const [standings, setStandings] = useState([]);
  const [standingsGroups, setStandingsGroups] = useState(null);
  const [standingsLoading, setStandingsLoading] = useState(true);

  const [matches, setMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setNotFound(false);

    getPublicTournamentApi(id)
      .then(({ data }) => {
        if (mounted) setTournament(data?.data || null);
      })
      .catch(() => {
        if (mounted) setNotFound(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    let mounted = true;
    setStandingsLoading(true);

    getPublicStandingsApi(id)
      .then(({ data }) => {
        if (!mounted) return;
        setStandings(data?.data?.standings || []);
        setStandingsGroups(data?.data?.groups || null);
      })
      .catch(() => {
        if (mounted) {
          setStandings([]);
          setStandingsGroups(null);
        }
      })
      .finally(() => {
        if (mounted) setStandingsLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  useEffect(() => {
    let mounted = true;
    setMatchesLoading(true);

    getPublicMatchesApi({ tournamentId: id, limit: 100 })
      .then(({ data }) => {
        if (mounted) setMatches(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => {
        if (mounted) setMatches([]);
      })
      .finally(() => {
        if (mounted) setMatchesLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [id]);

  const sortedMatches = useMemo(
    () => [...matches].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [matches]
  );

  if (loading) {
    return (
      <main className="min-h-screen">
        <div className="h-80 animate-pulse bg-[#0B1F17]" />
        <div className="container-page py-12">
          <div className="h-96 animate-pulse rounded-[2rem] bg-white shadow-sm" />
        </div>
      </main>
    );
  }

  if (notFound || !tournament) {
    return (
      <main className="flex min-h-[60vh] items-center justify-center px-4">
        <div className="text-center">
          <div className="mx-auto grid h-20 w-20 place-items-center rounded-[1.75rem] bg-slate-100 text-slate-300">
            <Icon name="trophy" className="h-10 w-10" />
          </div>
          <h1 className="mt-6 text-2xl font-black text-slate-900">تورنمنت پیدا نشد</h1>
          <p className="mt-2 text-sm text-slate-500">
            تورنمنتی که دنبال آن هستید وجود ندارد یا حذف شده است.
          </p>
          <Link
            to="/tournaments"
            className="mt-6 inline-flex rounded-xl bg-emerald-600 px-6 py-3 text-sm font-black text-white transition hover:bg-emerald-700"
          >
            بازگشت به فهرست تورنمنت‌ ها
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen overflow-hidden">
      <TournamentHero tournament={tournament} />

      <section className="container-page relative py-10 sm:py-14">
        <TabBar active={activeTab} onChange={setActiveTab} />

        {activeTab === "overview" && <OverviewTab tournament={tournament} />}
        {activeTab === "standings" && (
          <StandingsTab
            standings={standings}
            groups={standingsGroups}
            loading={standingsLoading}
          />
        )}
        {activeTab === "matches" && (
          <MatchesTab matches={sortedMatches} loading={matchesLoading} />
        )}
      </section>
    </main>
  );
}